import { useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../context/LanguageContext/LanguageContext';
import { usePageView } from '../../hooks/usePageView';
import CaptchaCanvas from './CaptchaCanvas';
import ContactMap from './ContactMap';
import Toast from '../../components/Toast/Toast';
import './Contact.css';

function Contact() {
  usePageView('contact');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    service: '',
    message: '',
  });
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const captchaRef = useRef(null);
  const captchaCodeRef = useRef('');
  const handleCaptchaCode = useCallback((code) => {
    captchaCodeRef.current = code;
  }, []);
  const { t } = useLanguage();
  const c = t.contact;

  const showToast = useCallback((type, msg) => setToast({ type, msg }), []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!/^1[3-9]\d{9}$/.test(formData.phone.trim())) {
      errors.phone = c.phoneError;
    }
    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      errors.email = c.emailError;
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    if (!privacyChecked) {
      showToast('error', c.privacyNotice);
      return;
    }
    if (
      captchaInput.trim().toLowerCase() !== captchaCodeRef.current.toLowerCase()
    ) {
      setCaptchaError(true);
      setCaptchaInput('');
      captchaRef.current?.refresh();
      return;
    }
    setCaptchaError(false);
    setSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        showToast('success', c.successMsg);
        setFormData({
          name: '',
          phone: '',
          email: '',
          company: '',
          service: '',
          message: '',
        });
        setCaptchaInput('');
        captchaRef.current?.refresh();
      } else {
        showToast('error', data.error || c.failMsg);
      }
    } catch {
      showToast('error', c.failMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const contacts = [
    {
      name: 'Cherry Chan（陈嘉慧）',
      title: 'General Manager',
      phone: '155 2133 9259',
      whatsapp: '155 2133 9259',
      email: 'cherry@fjhanyang.com',
      email2: 'oversales@szshowjoy.com',
      address: c.teamAddress,
    },
    {
      name: 'Daisy Liu（刘湘）',
      title: 'Assistant Department Director',
      phone: '135 3092 5725',
      whatsapp: '135 5685 6098',
      email: 'asst01@fjhanyang.com',
      address: c.teamAddress,
    },
    {
      name: 'Lucy（冯青青）',
      title: 'Customer Complaints Department',
      phone: '189 7419 4148',
      whatsapp: '189 7419 4148',
      email: 'asst03@fjhanyang.com',
      address: c.teamAddress,
    },
  ];

  return (
    <div className="contact-page">
      <Helmet>
        <title>联系我们 - 首嘉/深圳瀚阳</title>
        <meta
          name="description"
          content="联系首嘉/深圳瀚阳，获取专业货运咨询与报价服务。电话：15521339259，邮箱：cherry@fjhanyang.com / oversales@szshowjoy.com。"
        />
        <meta property="og:title" content="联系我们 - 首嘉/深圳瀚阳" />
        <meta property="og:description" content="联系首嘉/深圳瀚阳，获取专业货运咨询与报价服务。电话：15521339259，邮箱：cherry@fjhanyang.com / oversales@szshowjoy.com。" />
        <meta property="og:url" content="https://www.sz-showjoy.com/contact" />
        <meta name="keywords" content="联系首嘉/深圳瀚阳,物流咨询,货运询价,物流公司电话,深圳物流联系方式" />
        <link rel="canonical" href="https://www.sz-showjoy.com/contact" />
      </Helmet>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="page-header">
        <div className="container">
          <h1>{c.pageTitle}</h1>
          <p>{c.pageSubtitle}</p>
        </div>
      </div>

      <section className="section team-contacts">
        <div className="container">
          <h2 className="section-title">{c.teamTitle || '联系我们的团队'}</h2>
          <div className="team-contacts-grid">
            {contacts.map((person) => (
              <div className="team-contact-card" key={person.email}>
                <div className="team-contact-avatar">{person.name.split('（')[1][0]}</div>
                <h3 className="team-contact-name">{person.name}</h3>
                {person.title && (
                  <p className="team-contact-title">{person.title}</p>
                )}
                <div className="team-contact-info">
                  <div className="team-contact-row">
                    <span className="team-contact-icon">📱</span>
                    <a href={`tel:+86${person.phone.replace(/\s/g, '')}`}>{person.phone}</a>
                  </div>
                  {person.whatsapp && (
                    <div className="team-contact-row">
                      <span className="team-contact-icon">💬</span>
                      <span>WhatsApp: {person.whatsapp}</span>
                    </div>
                  )}
                  <div className="team-contact-row">
                    <span className="team-contact-icon">📧</span>
                    <a href={`mailto:${person.email}`}>{person.email}</a>
                  </div>
                  {person.email2 && (
                    <div className="team-contact-row">
                      <span className="team-contact-icon">📧</span>
                      <a href={`mailto:${person.email2}`}>{person.email2}</a>
                    </div>
                  )}
                  <div className="team-contact-row">
                    <span className="team-contact-icon">📍</span>
                    <span>{person.address}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h2>{c.contactInfo}</h2>
              <p className="contact-intro">{c.intro}</p>

              <div className="info-items">
                <div className="info-item">
                  <div className="info-icon">📞</div>
                  <div>
                    <h3>{c.hotline}</h3>
                    <p>15521339259</p>
                    <span>{c.hotlineHours}</span>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-icon">📧</div>
                  <div>
                    <h3>{c.email}</h3>
                    <p>cherry@fjhanyang.com</p>
                    <p>oversales@szshowjoy.com</p>
                    <span>{c.emailReply}</span>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-icon">📍</div>
                  <div>
                    <h3>{c.address}</h3>
                    <p>{c.addressValue}</p>
                    {/* <span>{c.addressNav}</span> */}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-icon">💬</div>
                  <div>
                    <h3>{c.onlineService}</h3>
                    <p>{c.wechat}</p>
                    {/* <span>{c.wechatScan}</span> */}
                  </div>
                </div>
              </div>

              <div className="office-hours">
                <h3>{c.businessHours}</h3>
                <div className="hours-list">
                  <div className="hours-item">
                    <span>{c.weekdays}</span>
                    <span>8:00 - 18:00</span>
                  </div>
                  <div className="hours-item">
                    <span>{c.weekends}</span>
                    <span>9:00 - 17:00</span>
                  </div>
                  <div className="hours-item">
                    <span>{c.holidays}</span>
                    <span>{c.onDuty}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <h2>{c.onlineConsult}</h2>
              <p>{c.consultDesc}</p>

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label>{c.name}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={c.namePlaceholder}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{c.phone}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={c.phonePlaceholder}
                    required
                  />
                  {fieldErrors.phone && (
                    <span className="field-error">{fieldErrors.phone}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>{c.emailLabel}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={c.emailPlaceholder}
                  />
                  {fieldErrors.email && (
                    <span className="field-error">{fieldErrors.email}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>{c.company}</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={c.companyPlaceholder}
                  />
                </div>
                <div className="form-group">
                  <label>{c.serviceType}</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{c.selectService}</option>
                    <option value="international">{c.intlService}</option>
                    <option value="warehouse">{c.warehouseService}</option>
                    <option value="supply-chain">{c.supplyChainService}</option>
                    <option value="other">{c.otherService}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{c.message}</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={c.messagePlaceholder}
                    rows="5"
                    required
                  ></textarea>
                </div>
                <div className="form-group">
                  <label>{c.captchaLabel}</label>
                  <div className="captcha-row">
                    <CaptchaCanvas
                      ref={captchaRef}
                      onCodeChange={handleCaptchaCode}
                    />
                    <input
                      type="text"
                      value={captchaInput}
                      onChange={(e) => {
                        setCaptchaInput(e.target.value);
                        setCaptchaError(false);
                      }}
                      placeholder={c.captchaPlaceholder}
                      maxLength={4}
                      required
                      autoComplete="off"
                    />
                  </div>
                  {captchaError && (
                    <span className="captcha-error">{c.captchaError}</span>
                  )}
                </div>
                <div className="privacy-checkbox-row">
                  <label className="privacy-label">
                    <input
                      type="checkbox"
                      checked={privacyChecked}
                      onChange={(e) => setPrivacyChecked(e.target.checked)}
                    />
                    <span>
                      {c.privacyCheckbox}
                      <button
                        type="button"
                        className="privacy-link"
                        onClick={() => setShowPrivacyModal(true)}
                      >
                        {c.privacyLink}
                      </button>
                    </span>
                  </label>
                </div>
                <button
                  type="submit"
                  className="btn-primary submit-btn"
                  disabled={submitting}
                >
                  {submitting ? c.submitting || '提交中...' : c.submit}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="map-section">
        <div className="container">
          <h2 className="section-title">{c.location}</h2>
          <ContactMap />
        </div>
      </section>

      {showPrivacyModal && (
        <div
          className="privacy-modal-overlay"
          onClick={() => setShowPrivacyModal(false)}
        >
          <div className="privacy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="privacy-modal-header">
              <h2 className="privacy-modal-title">{c.privacyModalTitle}</h2>
              <button
                type="button"
                className="privacy-modal-close"
                onClick={() => setShowPrivacyModal(false)}
              >
                ×
              </button>
            </div>
            <div className="privacy-modal-body">
              <h3>{c.privacyModalSubtitle}</h3>
              <h4>{c.privacyModalIntroTitle}</h4>
              <p>{c.privacyModalIntro}</p>
              <h4>{c.privacyModalS1Title}</h4>
              <p>{c.privacyModalS1}</p>
              <h4>{c.privacyModalS2Title}</h4>
              <p>{c.privacyModalS2}</p>
              <h4>{c.privacyModalS3Title}</h4>
              <p>{c.privacyModalS3}</p>
              <h4>{c.privacyModalS4Title}</h4>
              <p>{c.privacyModalS4}</p>
              <h4>{c.privacyModalS5Title}</h4>
              <p>{c.privacyModalS5}</p>
              <h4>{c.privacyModalS6Title}</h4>
              <p>{c.privacyModalS6}</p>
            </div>
            <button
              type="button"
              className="privacy-modal-confirm"
              onClick={() => {
                setPrivacyChecked(true);
                setShowPrivacyModal(false);
              }}
            >
              {c.privacyModalConfirm}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Contact;
